// Reservations dashboard section: KPI row + over-time chart + handling-time distribution
// histogram + sortable locations table. Role (my company / partner) switch shown when both
// roles are available.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import { PartnerRolesAvailabilityDTO, ReservationLocationRowDTO, ReservationStatisticsDTO } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { formatNumber } from '@utils/utils.tsx';
import { useDashboardPref } from '../dashboardPrefs.tsx';
import { formatPercent, useDashboardDuration } from '../dashboardFormat.ts';
import SvgCalendar from '@icons/calendar.svg?react';
import DashboardSelect from '../components/DashboardSelect.tsx';
import DashboardSection from '../components/DashboardSection.tsx';
import DashboardKpiCard from '../components/DashboardKpiCard.tsx';
import DashboardTable, { DashboardColumn } from '../components/DashboardTable.tsx';
import DashboardCard from '../components/DashboardCard.tsx';
import DashboardChartTitle from '../components/DashboardChartTitle.tsx';
import RingilChart from '../charts/RingilChart.tsx';
import { axisTooltipFormatter, barValueLabel, chartColors } from '../charts/chartTheme.ts';
import { isChartDataEmpty, timeSeriesZoom } from '../charts/chartOptions.ts';
import { PartnerRole, ReservationLocationSortBy, SortDir, StatsRange } from '../dashboardTypes.ts';

interface ReservationsSectionProps {
  availability: PartnerRolesAvailabilityDTO;
}

// Which metric the over-time bar chart plots, and which distribution the histogram shows.
type OverTimeMetric = 'COUNT' | 'SCHEDULED' | 'ACTUAL';
type DistributionMetric = 'SCHEDULED' | 'ACTUAL';

const ReservationsSection: React.FC<ReservationsSectionProps> = ({ availability }) => {
  const { tr } = useTranslationLgs();
  const { getDashboardSectionReservations, getDashboardSectionReservationsLocations } = useContext(ApiContext);
  const formatDuration = useDashboardDuration();

  const bothRoles = availability.myCompany && availability.asPartner;
  const [storedRole, setRole] = useDashboardPref<PartnerRole>('reservations.role', availability.myCompany ? PartnerRole.MY_COMPANY : PartnerRole.PARTNER);
  // The stored role may no longer be available (permissions changed, different account on the same
  // browser) — clamp it to an available one, otherwise the section queries an empty variant.
  const role = bothRoles ? storedRole : availability.myCompany ? PartnerRole.MY_COMPANY : PartnerRole.PARTNER;
  // Shared across all sections so the 30 days / 6 months switch ties them together.
  const [range, setRange] = useDashboardPref<StatsRange>('dashboard.range', StatsRange.D30);
  const [sortBy, setSortBy] = useDashboardPref<ReservationLocationSortBy>('reservations.sortBy', ReservationLocationSortBy.RESERVATIONS);
  const [sortDir, setSortDir] = useDashboardPref<SortDir>('reservations.sortDir', SortDir.DESC);
  const [overTimeMetric, setOverTimeMetric] = useDashboardPref<OverTimeMetric>('reservations.overTimeMetric', 'COUNT');
  const [distributionMetric, setDistributionMetric] = useDashboardPref<DistributionMetric>('reservations.distributionMetric', 'SCHEDULED');

  const [stats, setStats] = useState<ReservationStatisticsDTO>();
  const [locations, setLocations] = useState<ReservationLocationRowDTO[]>([]);

  // "Manipulation time" (actual handling) and the locations table are customer-side views, so
  // they're hidden in the supplier (partner) role. A previously-stored ACTUAL metric selection is
  // clamped back to a valid option when viewing as a partner.
  const isCustomer = role === PartnerRole.MY_COMPANY;
  const overTimeMetricEff: OverTimeMetric = !isCustomer && overTimeMetric === 'ACTUAL' ? 'COUNT' : overTimeMetric;
  const distributionMetricEff: DistributionMetric = !isCustomer && distributionMetric === 'ACTUAL' ? 'SCHEDULED' : distributionMetric;

  useEffect(() => {
    getDashboardSectionReservations(role, range, data => setStats(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range]);

  useEffect(() => {
    // Locations table is customer-only (see below), so don't fetch it as a partner.
    if (role === PartnerRole.MY_COMPANY) getDashboardSectionReservationsLocations(role, range, sortBy, sortDir, data => setLocations(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range, sortBy, sortDir]);

  const onSort = (key: ReservationLocationSortBy) => {
    if (key === sortBy) setSortDir(sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
    else {
      setSortBy(key);
      setSortDir(SortDir.DESC);
    }
  };

  const comparison = range === StatsRange.D30 ? tr('dashboard.vsPrior30Days', 'vs. prior 30 days') : tr('dashboard.vsPrior6Months', 'vs. prior 6 months');
  const countLabel = tr('dashboard.reservations.count', 'Reservations');
  const scheduledLabel = tr('dashboard.reservations.avgScheduled', 'Avg scheduled time');
  const actualLabel = tr('dashboard.reservations.avgActual', 'Avg manipulation time');
  const numberLegend = tr('dashboard.reservations.numberOfReservations', 'Number of reservations');
  const durationLegend = tr('dashboard.reservations.durationMinutes', 'Duration in minutes');
  // The over-time chart plots the average duration per bucket, so its legend is explicit about it.
  const avgDurationLegend = tr('dashboard.reservations.avgDurationMinutes', 'Avg. duration in minutes');
  const minutesUnit = tr('dashboard.reservations.minutes', 'min');

  // Left chart: one series at a time (count / scheduled / actual), chosen by the metric switch.
  const overTimeOption = useMemo(() => {
    const ot = stats?.overTime ?? [];
    const isMinutes = overTimeMetricEff !== 'COUNT';
    const data = ot.map(b => (overTimeMetricEff === 'COUNT' ? b.count : overTimeMetricEff === 'SCHEDULED' ? b.avgScheduledMinutes ?? null : b.avgActualMinutes ?? null));
    const seriesName = isMinutes ? avgDurationLegend : numberLegend;
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: axisTooltipFormatter(v => formatNumber(v, 0, 0)),
      },
      legend: { top: 0, right: 0, data: [seriesName] },
      grid: { left: 0, right: 8, top: 40, bottom: 56, containLabel: true },
      ...timeSeriesZoom,
      xAxis: { type: 'category', data: ot.map(b => b.bucket) },
      yAxis: { type: 'value', name: isMinutes ? minutesUnit : '', nameLocation: 'end', nameGap: 14, nameTextStyle: { align: 'right', color: chartColors.tertiary } },
      series: [
        {
          name: seriesName,
          type: 'bar',
          color: chartColors.draft,
          data,
          barMaxWidth: 40,
          // Bar labels show the bare rounded number for every metric; the y-axis carries the unit (min).
          label: barValueLabel(v => formatNumber(Math.round(v), 0, 0)),
        },
      ],
    };
  }, [stats, overTimeMetricEff, numberLegend, avgDurationLegend, minutesUnit]);

  // Right chart: one distribution at a time (scheduled / actual handling time), chosen by switch.
  const distributionOption = useMemo(() => {
    const dist = (distributionMetricEff === 'SCHEDULED' ? stats?.scheduledTimeDistribution : stats?.actualTimeDistribution) ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: axisTooltipFormatter(v => formatNumber(v, 0)),
      },
      legend: { top: 0, right: 0, data: [durationLegend] },
      grid: { left: 0, right: 8, top: 40, bottom: 24, containLabel: true },
      xAxis: { type: 'category', data: dist.map(b => b.label) },
      yAxis: { type: 'value' },
      series: [
        {
          name: durationLegend,
          type: 'bar',
          color: chartColors.error,
          data: dist.map(b => b.count),
          barMaxWidth: 40,
          label: barValueLabel(v => formatNumber(v, 0)),
        },
      ],
    };
  }, [stats, distributionMetricEff, durationLegend]);

  const overTimeEmpty = isChartDataEmpty(
    (stats?.overTime ?? []).map(b => (overTimeMetricEff === 'COUNT' ? b.count : overTimeMetricEff === 'SCHEDULED' ? b.avgScheduledMinutes : b.avgActualMinutes)),
  );
  const distributionEmpty = isChartDataEmpty(((distributionMetricEff === 'SCHEDULED' ? stats?.scheduledTimeDistribution : stats?.actualTimeDistribution) ?? []).map(b => b.count));

  const columns: DashboardColumn<ReservationLocationRowDTO, ReservationLocationSortBy>[] = [
    { key: 'location', label: tr('dashboard.reservations.location', 'Location'), sortKey: ReservationLocationSortBy.LOCATION, render: r => r.locationName ?? '—' },
    { key: 'ramp', label: tr('dashboard.reservations.ramp', 'Ramp'), sortKey: ReservationLocationSortBy.RAMP, render: r => r.rampName ?? '—' },
    { key: 'reservations', label: countLabel, sortKey: ReservationLocationSortBy.RESERVATIONS, align: 'right', render: r => formatNumber(r.reservations, 0) },
    { key: 'scheduled', label: scheduledLabel, sortKey: ReservationLocationSortBy.SCHEDULED_TIME, align: 'right', render: r => formatDuration(r.avgScheduledMinutes) },
    { key: 'actual', label: actualLabel, sortKey: ReservationLocationSortBy.ACTUAL_TIME, align: 'right', render: r => formatDuration(r.avgActualMinutes) },
  ];

  const controls = (
    <>
      {bothRoles && (
        <DashboardSelect
          value={role}
          onChange={setRole}
          options={[
            { code: PartnerRole.MY_COMPANY, label: tr('dashboard.role.myCompany', 'My company') },
            { code: PartnerRole.PARTNER, label: tr('dashboard.role.partner', 'Partners') },
          ]}
        />
      )}
      <DashboardSelect
        value={range}
        onChange={setRange}
        options={[
          { code: StatsRange.D30, label: tr('dashboard.range.d30', '30 days') },
          { code: StatsRange.M6, label: tr('dashboard.range.m6', '6 months') },
        ]}
      />
    </>
  );

  return (
    <DashboardSection title={tr('dashboard.reservations.title', 'Reservations')} icon={<SvgCalendar />} controls={controls}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <DashboardKpiCard label={tr('dashboard.reservations.total', 'Total reservations')} value={formatNumber(stats?.totalReservations?.current ?? 0, 0)} changePct={stats?.totalReservations?.changePct} comparisonLabel={comparison} />
        <DashboardKpiCard label={scheduledLabel} value={formatDuration(stats?.scheduledTimeMinutes?.current)} changePct={stats?.scheduledTimeMinutes?.changePct} comparisonLabel={comparison} />
        {stats?.actualHandlingTimeMinutes && <DashboardKpiCard label={actualLabel} value={formatDuration(stats.actualHandlingTimeMinutes.current)} changePct={stats.actualHandlingTimeMinutes.changePct} comparisonLabel={comparison} />}
        {stats?.rampUtilization && <DashboardKpiCard label={tr('dashboard.reservations.rampUtilization', 'Ramp utilization')} value={formatPercent(stats.rampUtilization.current)} changePct={stats.rampUtilization.changePct} comparisonLabel={comparison} />}
        {stats?.finishedPct && <DashboardKpiCard label={tr('dashboard.reservations.finished', 'Finished')} value={formatPercent(stats.finishedPct.current)} changePct={stats.finishedPct.changePct} comparisonLabel={comparison} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardCard>
          <DashboardChartTitle title={tr('dashboard.reservations.title', 'Reservations')} className="mb-3" />
          <DashboardSelect
            value={overTimeMetricEff}
            onChange={setOverTimeMetric}
            options={(
              [
                { code: 'COUNT', label: tr('dashboard.reservations.metric.reservations', 'Reservations') },
                { code: 'SCHEDULED', label: tr('dashboard.reservations.metric.scheduled', 'Scheduled time') },
                { code: 'ACTUAL', label: tr('dashboard.reservations.metric.manipulation', 'Manipulation time') },
              ] as { code: OverTimeMetric; label: string }[]
            ).filter(o => isCustomer || o.code !== 'ACTUAL')}
          />
          <div className="mt-3">
            <RingilChart option={overTimeOption} height={300} notMerge isEmpty={overTimeEmpty} />
          </div>
        </DashboardCard>
        <DashboardCard>
          <DashboardChartTitle title={tr('dashboard.reservations.title', 'Reservations')} className="mb-3" />
          <DashboardSelect
            value={distributionMetricEff}
            onChange={setDistributionMetric}
            options={(
              [
                { code: 'SCHEDULED', label: tr('dashboard.reservations.dist.scheduled', 'Scheduled time') },
                { code: 'ACTUAL', label: tr('dashboard.reservations.dist.actual', 'Manipulation time') },
              ] as { code: DistributionMetric; label: string }[]
            ).filter(o => isCustomer || o.code !== 'ACTUAL')}
          />
          <div className="mt-3">
            <RingilChart option={distributionOption} height={300} notMerge isEmpty={distributionEmpty} />
          </div>
        </DashboardCard>
      </div>

      {/* Locations breakdown is a customer-side view — hidden in the supplier (partner) role. */}
      {isCustomer && (
        <DashboardCard>
          <DashboardChartTitle title={tr('dashboard.reservations.locations', 'Locations')} />
          <DashboardTable columns={columns} rows={locations} rowKey={(r, i) => `${r.locationId ?? ''}-${r.rampId ?? ''}-${i}`} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </DashboardCard>
      )}
    </DashboardSection>
  );
};

export default ReservationsSection;